import { useEffect, useRef, useState } from 'react';
import { refreshAccessToken, tokenStore } from '@/lib/api';
import { notifyForSseEvent } from '@/lib/notifications';

export type SseStatus = 'idle' | 'connecting' | 'connected' | 'disconnected';

export interface SseEvent<T = unknown> {
  id: string;
  type: string;
  data: T;
}

type Listener<T = unknown> = (event: SseEvent<T>) => void;

interface UseSseOptions {
  enabled?: boolean;
  onEvent?: Listener;
}

/**
 * Native EventSource handles auto-reconnect + Last-Event-ID. We just
 * surface connection state and forward typed events to the listener.
 */
export function useSse(options: UseSseOptions = {}) {
  const { enabled = true, onEvent } = options;
  const [status, setStatus] = useState<SseStatus>('idle');
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  useEffect(() => {
    if (!enabled) return;

    let es: EventSource | null = null;
    let cancelled = false;
    // Tracks whether we've already burned our one silent-refresh attempt
    // on this mount. Two consecutive errors AFTER a refresh means the new
    // token is also being rejected (dead session / clock skew) — we stop
    // hammering and let the api() 401 handler bounce the user to /login
    // on the next XHR.
    let refreshedOnce = false;

    const KNOWN_EVENTS = [
      'otp_ready',
      'request_pending',
      'request_approved',
      'request_declined',
      'request_timed_out',
      'session_activated',
      'session_expired',
      'beneficiary_hold',
      'ambiguous_hold',
      'parked',
      'pending_review',
      'transaction_confirmed',
      'transaction_unattributed',
      'transaction_fx_matched',
      'attribution_reminder',
      'attribution_escalated',
      'wallet_variance_flagged',
      'payment_action_required',
      'hold_created',
      'hold_charged',
      'hold_cancel_requested',
      'wallet_freed',
    ] as const;

    const handler = (type: string) => (ev: MessageEvent) => {
      try {
        const data = JSON.parse(ev.data);
        // Fire OS-level browser notification (works when tab is minimized).
        notifyForSseEvent(type, data);
        onEventRef.current?.({ id: ev.lastEventId, type, data });
      } catch {
        // ignore malformed
      }
    };

    const connect = () => {
      if (cancelled) return;
      const token = tokenStore.getAccess();
      if (!token) {
        setStatus('disconnected');
        return;
      }
      setStatus('connecting');
      const url = `/api/sse/otp-stream?token=${encodeURIComponent(token)}`;
      es = new EventSource(url);

      es.onopen = () => {
        refreshedOnce = false;
        setStatus('connected');
      };

      // EventSource fires `onerror` for BOTH network drops AND 401s, but
      // hides the HTTP status from JS. So on any error we assume the
      // token may have expired (which is by far the most common cause of
      // silent SSE death — see the 2026-07-02 ₹50k OTP incident where
      // Gautam's dashboard sat idle for 6 min after his JWT expired).
      // Close the socket first — otherwise EventSource keeps retrying
      // the same expired URL in the background — then refresh + reopen.
      es.onerror = () => {
        setStatus('disconnected');
        if (cancelled || refreshedOnce) return;
        refreshedOnce = true;
        es?.close();
        es = null;
        void refreshAccessToken().then((ok) => {
          if (!cancelled && ok) connect();
        });
      };

      for (const t of KNOWN_EVENTS) es.addEventListener(t, handler(t));
      es.onmessage = handler('message');
    };

    connect();

    return () => {
      cancelled = true;
      es?.close();
      setStatus('idle');
    };
  }, [enabled]);

  return { status };
}
